import { type FC, MouseEventHandler, useId } from "react";
import { useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";

import { t } from "i18n/i18n";
import { type TrackingProps, trackEvent } from "utils/amplitude";

type LabeledTextInputProps = React.InputHTMLAttributes<HTMLInputElement> &
  TrackingProps & {
    wrapperClassName?: string;
    name: string;
    label: string;
    postLabel?: React.ReactNode;
    required?: boolean;
  };

export const LabeledTextInput: FC<LabeledTextInputProps> = function LabeledTextInputInternal({
  ref,
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  postLabel,
  required = false,
  eventName,
  eventProps,
  onClick,
  ...props
}: LabeledTextInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const randomId = useId();
  const formState = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  const labelNode = (
    <label className="font-medium text-black-tint-20" htmlFor={id}>
      {label}
    </label>
  );

  const clickHandler: MouseEventHandler<HTMLInputElement> | undefined = eventName
    ? (e) => {
        trackEvent(eventName, { eventCategory: "User Input", ...eventProps });
        if (onClick) {
          onClick(e);
        }
      }
    : onClick;

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="flex items-center justify-between">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && <div className="font-normal text-black-tint-40">{t("common.optional")}</div>}
      </div>
      <input
        aria-errormessage={formState.errors[name] ? errorId : undefined}
        aria-invalid={formState.errors[name] ? "true" : "false"}
        className={twMerge(
          "rounded border border-black-tint-90 px-1.5 py-1 font-normal aria-invalid:border-red",
          className,
        )}
        id={id}
        name={name}
        ref={ref}
        onClick={clickHandler}
        {...props}
      />
      <ErrorMessageBanner id={errorId} name={name} />
    </div>
  );
};
